import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Agendamento() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    servico: '',
    tipoEmpresa: '',
    dataPreferida: '',
    horarioPreferido: '',
    observacoes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    // Update page title and meta description for SEO
    document.title = 'Agendamento Online - N&N Soluções Empresariais | Agende sua Consulta';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Agende sua consulta online com a N&N Soluções Empresariais. Processo simples em 4 etapas. Atendimento de segunda a sexta, 8h às 18h. WhatsApp (62) 99391-1090.');
    }

    // Add structured data for appointment page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Agendamento de Consulta",
      "description": "Agende sua consulta de contabilidade online",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/agendamento`,
      "mainEntity": {
        "@type": "Service",
        "name": "Consulta de Contabilidade",
        "provider": {
          "@type": "Organization",
          "name": "N&N Soluções Empresariais"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Av. Goiás, 606",
          "addressLocality": "Goiânia",
          "addressRegion": "GO",
          "postalCode": "74015-010",
          "addressCountry": "BR"
        },
        "availableChannel": {
          "@type": "ServiceChannel",
          "serviceType": "Consulta Presencial e Online",
          "availableLanguage": "pt-BR"
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.text.includes('Agendamento de Consulta')) {
          script.remove();
        }
      });
    };
  }, []);

  const steps = [
    { number: 1, title: 'Primeiro Contato', description: 'Informações básicas' },
    { number: 2, title: 'Detalhes do Serviço', description: 'Tipo de atendimento' },
    { number: 3, title: 'Agendamento', description: 'Data e horário' },
    { number: 4, title: 'Confirmação', description: 'Revisão final' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3orgs4uve6meee39lp0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappMessage = `Olá! Gostaria de agendar uma consulta.%0A%0ANome: ${formData.nome}%0AEmpresa: ${formData.empresa}%0AServiço: ${formData.servico}%0AData preferida: ${formData.dataPreferida}%0AHorário: ${formData.horarioPreferido}`;

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="https://nensolucoes.com.br/nenlogo-escura.png" 
                alt="N&N Soluções Empresariais" 
                className="h-16 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => navigate('/')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Home</button>
              <button onClick={() => navigate('/servicos')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Serviços</button>
              <button onClick={() => navigate('/sobre')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Sobre</button>
              <button onClick={() => navigate('/contato')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Contato</button>
              <button onClick={() => navigate('/agendamento')} className="font-semibold cursor-pointer" style={{ color: '#72142B' }}>Agendamento</button>
            </nav>
            <a 
              href="https://wa.me/5562993911090"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white px-6 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#72142B' }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative py-20 pt-32 overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20business%20meeting%20room%20with%20modern%20conference%20table%2C%20elegant%20scheduling%20calendar%20on%20wall%2C%20warm%20professional%20lighting%2C%20burgundy%20and%20cream%20color%20scheme%2C%20sophisticated%20office%20environment%20conveying%20organization%20and%20planning%20excellence&width=1920&height=600&seq=agendamento-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#72142B]/80 via-[#72142B]/40 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm text-white" style={{ backgroundColor: '#72142B' }}>
              ✨ Agendamento Online
            </span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white">
            Agende sua
            <span className="block" style={{ color: '#FAEBEA' }}>Consulta</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed max-w-4xl mx-auto">
            Processo simples e rápido para agendar sua reunião conosco
          </p>
        </div>
      </section>

      {/* Processo de Agendamento */}
      <section className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
              Como funciona
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
              Processo de agendamento
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Siga os passos abaixo para agendar sua consulta de forma rápida e prática
            </p>
          </div>

          {/* Steps Progress */}
          <div className="mb-12">
            <div className="flex justify-center items-center space-x-4 overflow-x-auto pb-4">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center">
                  <div className="flex flex-col items-center min-w-0">
                    <div 
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold mb-2 ${
                        currentStep >= step.number ? 'opacity-100' : 'opacity-50'
                      }`}
                      style={{ backgroundColor: '#72142B' }}
                    >
                      {currentStep > step.number ? (
                        <i className="ri-check-line text-lg"></i>
                      ) : (
                        step.number
                      )}
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-sm" style={{ color: '#72142B' }}>{step.title}</div>
                      <div className="text-xs text-gray-600">{step.description}</div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div 
                      className={`w-16 h-1 mx-4 ${
                        currentStep > step.number ? 'opacity-100' : 'opacity-30'
                      }`}
                      style={{ backgroundColor: '#72142B' }}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <form id="agendamento-form" data-readdy-form onSubmit={handleSubmit}>
                {/* Step 1: Informações Básicas */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#72142B' }}>Primeiro Contato</h3>
                      <p className="text-gray-600">Vamos começar com suas informações básicas</p>
                    </div>
                    
                    <div>
                      <label htmlFor="nome" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        Nome completo *
                      </label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                        placeholder="Seu nome completo"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        E-mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                        placeholder="seu@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefone" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        Telefone/WhatsApp *
                      </label>
                      <input
                        type="tel"
                        id="telefone"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                        placeholder="(00) 00000-0000"
                      />
                    </div>

                    <div>
                      <label htmlFor="empresa" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        Nome da empresa
                      </label>
                      <input
                        type="text"
                        id="empresa"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                        placeholder="Nome da sua empresa"
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: Detalhes do Serviço */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#72142B' }}>Detalhes do Serviço</h3>
                      <p className="text-gray-600">Conte-nos sobre sua necessidade</p>
                    </div>

                    <div>
                      <label htmlFor="servico" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        Serviço de interesse *
                      </label>
                      <select
                        id="servico"
                        name="servico"
                        value={formData.servico}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Selecione um serviço</option>
                        <option value="contabilidade">Contabilidade Completa</option>
                        <option value="bpo-financeiro">BPO Financeiro</option>
                        <option value="consultoria-empresarial">Consultoria Empresarial</option>
                        <option value="departamento-pessoal">Departamento Pessoal</option>
                        <option value="consultoria-financeira">Consultoria Financeira</option>
                        <option value="declaracao-impostos">Declaração de Impostos</option>
                        <option value="abertura-empresa">Abertura de Empresa</option>
                        <option value="outros">Outros</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="tipoEmpresa" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        Tipo de empresa
                      </label>
                      <select
                        id="tipoEmpresa"
                        name="tipoEmpresa"
                        value={formData.tipoEmpresa}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Selecione o tipo</option>
                        <option value="mei">MEI</option>
                        <option value="microempresa">Microempresa</option>
                        <option value="pequena-empresa">Pequena Empresa</option>
                        <option value="media-empresa">Média Empresa</option>
                        <option value="pessoa-fisica">Pessoa Física</option>
                        <option value="profissional-liberal">Profissional Liberal</option>
                        <option value="nao-tenho-empresa">Não tenho empresa ainda</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="observacoes" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        Observações adicionais
                      </label>
                      <textarea
                        id="observacoes"
                        name="observacoes"
                        value={formData.observacoes}
                        onChange={handleInputChange}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm resize-none"
                        placeholder="Conte-nos mais detalhes sobre sua necessidade..."
                      ></textarea>
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {formData.observacoes.length}/500 caracteres
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Agendamento */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#72142B' }}>Agendamento</h3>
                      <p className="text-gray-600">Escolha a melhor data e horário para você</p>
                    </div>

                    <div>
                      <label htmlFor="dataPreferida" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        Data preferida *
                      </label>
                      <input
                        type="date"
                        id="dataPreferida"
                        name="dataPreferida"
                        value={formData.dataPreferida}
                        onChange={handleInputChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="horarioPreferido" className="block text-sm font-medium mb-2" style={{ color: '#72142B' }}>
                        Horário preferido *
                      </label>
                      <select
                        id="horarioPreferido"
                        name="horarioPreferido"
                        value={formData.horarioPreferido}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Selecione um horário</option>
                        <option value="08:00">08:00</option>
                        <option value="09:00">09:00</option>
                        <option value="10:00">10:00</option>
                        <option value="11:00">11:00</option>
                        <option value="14:00">14:00</option>
                        <option value="15:00">15:00</option>
                        <option value="16:00">16:00</option>
                        <option value="17:00">17:00</option>
                      </select>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#FAEBEA' }}>
                      <div className="flex items-start">
                        <i className="ri-information-line text-xl mr-3 mt-1" style={{ color: '#72142B' }}></i>
                        <div>
                          <h4 className="font-semibold mb-2" style={{ color: '#72142B' }}>Informações importantes:</h4>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• Horário de atendimento: Segunda a sexta, 8h às 18h</li>
                            <li>• A consulta tem duração aproximada de 1 hora</li>
                            <li>• Confirmaremos o agendamento por WhatsApp</li>
                            <li>• Reunião pode ser presencial ou online</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Confirmação */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#72142B' }}>Confirmação</h3>
                      <p className="text-gray-600">Revise suas informações antes de finalizar</p>
                    </div>

                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-2" style={{ color: '#72142B' }}>Dados pessoais:</h4>
                        <p><strong>Nome:</strong> {formData.nome}</p>
                        <p><strong>E-mail:</strong> {formData.email}</p>
                        <p><strong>Telefone:</strong> {formData.telefone}</p>
                        {formData.empresa && <p><strong>Empresa:</strong> {formData.empresa}</p>}
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-2" style={{ color: '#72142B' }}>Serviço:</h4>
                        <p><strong>Serviço de interesse:</strong> {formData.servico}</p>
                        {formData.tipoEmpresa && <p><strong>Tipo de empresa:</strong> {formData.tipoEmpresa}</p>}
                        {formData.observacoes && <p><strong>Observações:</strong> {formData.observacoes}</p>}
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-2" style={{ color: '#72142B' }}>Agendamento:</h4>
                        <p><strong>Data:</strong> {new Date(formData.dataPreferida).toLocaleDateString('pt-BR')}</p>
                        <p><strong>Horário:</strong> {formData.horarioPreferido}</p>
                      </div>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="text-center p-6 rounded-lg" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
                        <i className="ri-check-circle-line text-4xl mb-4"></i>
                        <h4 className="text-xl font-bold mb-2">Agendamento enviado com sucesso!</h4>
                        <p className="mb-4">Entraremos em contato em breve para confirmar sua consulta.</p>
                        <a
                          href={`https://wa.me/5562993911090?text=${whatsappMessage}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-white px-6 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer font-semibold"
                          style={{ backgroundColor: '#72142B' }}
                        >
                          Confirmar pelo WhatsApp
                        </a>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="text-center p-4 bg-red-100 text-red-700 rounded-lg">
                        <i className="ri-error-warning-line text-2xl mb-2"></i>
                        <p className="font-semibold">Erro ao enviar agendamento</p>
                        <p className="text-sm">Tente novamente ou entre em contato pelo WhatsApp.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className="px-6 py-3 border-2 rounded-full transition-colors whitespace-nowrap cursor-pointer font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: '#72142B', color: '#72142B' }}
                    onMouseEnter={(e) => currentStep > 1 && (e.target.style.backgroundColor = '#72142B', e.target.style.color = 'white')}
                    onMouseLeave={(e) => currentStep > 1 && (e.target.style.backgroundColor = 'transparent', e.target.style.color = '#72142B')}
                  >
                    Voltar
                  </button>

                  {currentStep < 4 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="text-white px-6 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer font-semibold"
                      style={{ backgroundColor: '#72142B' }}
                      onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      Próximo
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting || submitStatus === 'success'}
                      className="text-white px-6 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#72142B' }}
                      onMouseEnter={(e) => !isSubmitting && submitStatus !== 'success' && (e.target.style.opacity = '0.8')}
                      onMouseLeave={(e) => !isSubmitting && submitStatus !== 'success' && (e.target.style.opacity = '1')}
                    >
                      {isSubmitting ? 'Enviando...' : submitStatus === 'success' ? 'Enviado!' : 'Finalizar agendamento'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Agendamento Rápido via WhatsApp */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
              Agendamento Rápido
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
              Prefere agendar pelo WhatsApp?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Clique no botão abaixo e agende sua consulta diretamente pelo WhatsApp de forma ainda mais rápida
            </p>
            <a
              href="https://wa.me/5562993911090?text=Olá! Gostaria de agendar uma consulta."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-white px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              style={{ backgroundColor: '#72142B' }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              <i className="ri-whatsapp-line mr-2"></i>
              Agendar pelo WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <img 
                src="https://nensolucoes.com.br/nenlogo-cinca.png" 
                alt="N&N Soluções Empresariais" 
                className="h-16 w-auto mb-4 cursor-pointer"
                onClick={() => navigate('/')}
              />
              <p className="text-gray-300 mb-6">
                Simplificando sua contabilidade para você focar no que realmente importa.
              </p>
              <div className="space-y-2">
                <p className="flex items-center text-gray-300">
                  <i className="ri-phone-line mr-3"></i>
                  WhatsApp: (62) 99391-1090
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-mail-line mr-3"></i>
                  contato@nnsolucoes.com.br
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-map-pin-line mr-3"></i>
                  Av. Goiás, 606 - Setor Central, Goiânia - GO
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Links Úteis</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Home</button></li>
                <li><button onClick={() => navigate('/servicos')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Serviços</button></li>
                <li><button onClick={() => navigate('/sobre')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Sobre</button></li>
                <li><button onClick={() => navigate('/contato')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Contato</button></li>
                <li><button onClick={() => navigate('/agendamento')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Agendamento</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Nossos Serviços</h4>
              <ul className="space-y-2">
                <li><span className="text-gray-300">Contabilidade</span></li>
                <li><span className="text-gray-300">BPO Financeiro</span></li>
                <li><span className="text-gray-300">Departamento Pessoal</span></li>
                <li><span className="text-gray-300">Consultoria Financeira</span></li>
                <li><span className="text-gray-300">Declaração de Impostos</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-300">
              © 2026 N&N Soluções Empresariais. Todos os direitos reservados. | 
              <a href="https://www.instagram.com/rodriigocoelho" target="_blank" rel="noopener noreferrer" className="hover:text-gray-100 ml-1 cursor-pointer" style={{ color: '#FAEBEA' }}>
                RS DIGITAL
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
